import type { CmmdAnchorKind, LinkedPullRequest } from "@cmmd-forge/contracts";
import type { DraftThreadEnvMode } from "../composerDraftStore";

/**
 * The context a caller may set on a draft.
 *
 * Every field is three-valued and the distinction is load-bearing: **absent**
 * means "leave whatever the draft already has", while an explicit `null` means
 * "clear it". That is why the callers test `options?.branch !== undefined`
 * rather than truthiness — a caller clearing a branch and a caller not
 * mentioning branches must not collapse into the same update.
 */
export interface NewThreadDraftContextOptions {
  readonly branch?: string | null;
  readonly worktreePath?: string | null;
  readonly envMode?: DraftThreadEnvMode;
  readonly anchorKind?: CmmdAnchorKind | null;
  readonly anchorCmmdId?: string | null;
  readonly cmmdTaskId?: string | null;
  readonly linkedPullRequest?: LinkedPullRequest | null;
}

/** Whether the caller mentioned any context at all. */
export function mentionsDraftContext(options: NewThreadDraftContextOptions | undefined): boolean {
  if (!options) return false;
  return (
    options.branch !== undefined ||
    options.worktreePath !== undefined ||
    options.envMode !== undefined ||
    options.anchorKind !== undefined ||
    options.anchorCmmdId !== undefined ||
    options.cmmdTaskId !== undefined ||
    options.linkedPullRequest !== undefined
  );
}

/**
 * The patch for an existing draft's context — only the mentioned fields.
 *
 * `branch` and `worktreePath` normalise to `null` when mentioned without a
 * value, because "set the branch to nothing" is a real instruction; the others
 * pass through as given.
 */
export function buildDraftContextPatch(options: NewThreadDraftContextOptions | undefined): {
  branch?: string | null;
  worktreePath?: string | null;
  envMode?: DraftThreadEnvMode;
  anchorKind?: CmmdAnchorKind | null;
  anchorCmmdId?: string | null;
  cmmdTaskId?: string | null;
  linkedPullRequest?: LinkedPullRequest | null;
} {
  if (!options) return {};
  return {
    ...(options.branch !== undefined ? { branch: options.branch ?? null } : {}),
    ...(options.worktreePath !== undefined ? { worktreePath: options.worktreePath ?? null } : {}),
    ...(options.envMode !== undefined ? { envMode: options.envMode } : {}),
    ...(options.anchorKind !== undefined ? { anchorKind: options.anchorKind } : {}),
    ...(options.anchorCmmdId !== undefined ? { anchorCmmdId: options.anchorCmmdId } : {}),
    ...(options.cmmdTaskId !== undefined ? { cmmdTaskId: options.cmmdTaskId } : {}),
    ...(options.linkedPullRequest !== undefined
      ? { linkedPullRequest: options.linkedPullRequest }
      : {}),
  };
}

/**
 * The subset that belongs on the logical project's draft-thread record.
 *
 * The CMMD anchor is deliberately not part of it. An anchor points a draft at a
 * board item; the logical-project record is about where work runs, and carrying
 * the anchor there would make a second, divergent home for it.
 */
export function buildDraftThreadContextPatch(
  options: NewThreadDraftContextOptions | undefined,
): Pick<
  ReturnType<typeof buildDraftContextPatch>,
  "branch" | "worktreePath" | "envMode" | "cmmdTaskId" | "linkedPullRequest"
> {
  const { branch, worktreePath, envMode, cmmdTaskId, linkedPullRequest } =
    buildDraftContextPatch(options);
  return {
    ...(branch !== undefined ? { branch } : {}),
    ...(worktreePath !== undefined ? { worktreePath } : {}),
    ...(envMode !== undefined ? { envMode } : {}),
    ...(cmmdTaskId !== undefined ? { cmmdTaskId } : {}),
    ...(linkedPullRequest !== undefined ? { linkedPullRequest } : {}),
  };
}

/**
 * The record for a draft that does not exist yet.
 *
 * Unlike a patch, this is a whole record, so the fields that mean "leave it
 * alone" when patching mean "start with nothing" here: an unmentioned branch
 * is `null`, not absent. `envMode` defaults to `local` because a draft with no
 * stated mode works in the project itself, and `runtimeMode` is the caller's
 * default rather than a choice this makes.
 */
export function buildNewDraftRecord<TThreadId, TRuntimeMode>(
  options: NewThreadDraftContextOptions | undefined,
  base: {
    readonly threadId: TThreadId;
    readonly createdAt: string;
    readonly runtimeMode: TRuntimeMode;
  },
) {
  return {
    threadId: base.threadId,
    createdAt: base.createdAt,
    runtimeMode: base.runtimeMode,
    branch: options?.branch ?? null,
    worktreePath: options?.worktreePath ?? null,
    envMode: options?.envMode ?? ("local" as const),
    ...(options?.anchorKind !== undefined ? { anchorKind: options.anchorKind } : {}),
    ...(options?.anchorCmmdId !== undefined ? { anchorCmmdId: options.anchorCmmdId } : {}),
    ...(options?.cmmdTaskId !== undefined ? { cmmdTaskId: options.cmmdTaskId } : {}),
    ...(options?.linkedPullRequest !== undefined
      ? { linkedPullRequest: options.linkedPullRequest }
      : {}),
  };
}
